/**
 * Pointer interaction: pan/zoom, freehand target drawing, and direct
 * manipulation of the linkage (drag a pivot and the relevant link lengths or
 * ground coordinates update live).
 */

import type { MechanismSpec } from "../kinematics/types.js";
import type { P2 } from "../synthesis/curve.js";
import type { CanvasView } from "./canvas.js";
import type { Mode, State } from "./state.js";

export interface InteractionHost {
  view: CanvasView;
  state: State;
  mutateSpec(fn: (s: MechanismSpec) => void): void;
  setTarget(pts: P2[] | null): void;
  setMode(m: Mode): void;
  requestRender(): void;
}

type HandleKind = "ground" | "driven" | "free" | "coupler";
interface Handle {
  kind: HandleKind;
  index: number;
  world: P2;
}

const sub = (a: P2, b: P2): P2 => [a[0] - b[0], a[1] - b[1]];
const dot = (a: P2, b: P2): number => a[0] * b[0] + a[1] * b[1];
const len = (a: P2): number => Math.hypot(a[0], a[1]) || 1e-12;

export function attachInteractions(host: InteractionHost): () => void {
  const el = host.view.canvas;

  let dragging:
    | { type: "pan"; lastX: number; lastY: number }
    | { type: "handle"; handle: Handle }
    | { type: "draw"; path: P2[] }
    | null = null;

  function handlesFor(state: State): Handle[] {
    const out: Handle[] = [];
    const spec = state.spec;
    const G = spec.grounds.length;
    spec.grounds.forEach((g, i) => out.push({ kind: "ground", index: i, world: [g[0], g[1]] }));
    if (state.pose) {
      out.push({ kind: "driven", index: G, world: state.pose.positions[G] });
      for (let i = 0; i < spec.free.length; i++) {
        out.push({ kind: "free", index: G + 1 + i, world: state.pose.positions[G + 1 + i] });
      }
      out.push({ kind: "coupler", index: -1, world: state.pose.coupler });
    }
    return out;
  }

  function pickHandle(px: number, py: number): Handle | null {
    const state = host.state;
    let best: Handle | null = null;
    let bestD = 12; // px
    for (const h of handlesFor(state)) {
      const s = host.view.toScreen(h.world);
      const d = Math.hypot(s[0] - px, s[1] - py);
      if (d < bestD) {
        bestD = d;
        best = h;
      }
    }
    return best;
  }

  function localXY(e: PointerEvent): [number, number] {
    const r = el.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top];
  }

  function applyHandleDrag(handle: Handle, world: P2): void {
    const state = host.state;
    const pose = state.pose;
    host.mutateSpec((spec) => {
      const G = spec.grounds.length;
      if (handle.kind === "ground") {
        spec.grounds[handle.index] = [world[0], world[1]];
        return;
      }
      if (handle.kind === "driven") {
        const base = spec.grounds[spec.driver.base];
        const rel = sub(world, base);
        spec.driver.length = Math.max(1e-3, len(rel));
        spec.driver.phase = Math.atan2(rel[1], rel[0]) - spec.driver.dir * state.driverAngle;
        return;
      }
      if (handle.kind === "free" && pose) {
        const jointIdx = handle.index;
        spec.bars.forEach((b, k) => {
          let other = -1;
          if (b[0] === jointIdx) other = b[1];
          else if (b[1] === jointIdx) other = b[0];
          if (other >= 0) {
            const op = pose.positions[other];
            spec.bars[k] = [b[0], b[1], Math.max(1e-3, Math.hypot(world[0] - op[0], world[1] - op[1]))];
          }
        });
        return;
      }
      if (handle.kind === "coupler" && pose) {
        const [ia, ib] = spec.bars[spec.coupler.bar];
        const a = pose.positions[ia];
        const b = pose.positions[ib];
        const uhat = [(b[0] - a[0]) / len(sub(b, a)), (b[1] - a[1]) / len(sub(b, a))] as P2;
        const perp = [-uhat[1], uhat[0]] as P2;
        const rel = sub(world, a);
        spec.coupler.u = dot(rel, uhat);
        spec.coupler.v = dot(rel, perp);
      }
      void G;
    });
  }

  function simplify(path: P2[], minScreenDist: number): P2[] {
    if (path.length < 2) return path;
    const out: P2[] = [path[0]];
    for (let i = 1; i < path.length; i++) {
      const a = host.view.toScreen(out[out.length - 1]);
      const b = host.view.toScreen(path[i]);
      if (Math.hypot(a[0] - b[0], a[1] - b[1]) >= minScreenDist) out.push(path[i]);
    }
    if (out.length < 2) out.push(path[path.length - 1]);
    return out;
  }

  const onPointerDown = (e: PointerEvent) => {
    const [px, py] = localXY(e);
    el.setPointerCapture(e.pointerId);

    if (host.state.mode === "draw") {
      dragging = { type: "draw", path: [host.view.toWorld(px, py)] };
      return;
    }
    const handle = pickHandle(px, py);
    if (handle) {
      dragging = { type: "handle", handle };
      host.state.playing = false;
    } else {
      dragging = { type: "pan", lastX: px, lastY: py };
    }
  };

  const onPointerMove = (e: PointerEvent) => {
    if (!dragging) {
      const [px, py] = localXY(e);
      el.style.cursor =
        host.state.mode === "draw"
          ? "crosshair"
          : pickHandle(px, py)
            ? "grab"
            : "default";
      return;
    }
    const [px, py] = localXY(e);
    if (dragging.type === "pan") {
      host.view.panPixels(px - dragging.lastX, py - dragging.lastY);
      dragging.lastX = px;
      dragging.lastY = py;
      host.requestRender();
    } else if (dragging.type === "handle") {
      applyHandleDrag(dragging.handle, host.view.toWorld(px, py));
      host.requestRender();
    } else if (dragging.type === "draw") {
      dragging.path.push(host.view.toWorld(px, py));
      host.state.target = simplify(dragging.path, 3);
      host.requestRender();
    }
  };

  const onPointerUp = (e: PointerEvent) => {
    if (dragging?.type === "draw") {
      const path = simplify(dragging.path, 5);
      if (path.length >= 4) {
        host.setTarget(path);
        host.view.fit(path, 0.6);
      }
      host.setMode("idle");
    }
    dragging = null;
    try {
      el.releasePointerCapture(e.pointerId);
    } catch {
      /* pointer already released */
    }
  };

  const onWheel = (e: WheelEvent) => {
    e.preventDefault();
    const [px, py] = localXY(e as unknown as PointerEvent);
    host.view.zoomAt(px, py, Math.exp(-e.deltaY * 0.0015));
    host.requestRender();
  };

  el.addEventListener("pointerdown", onPointerDown);
  el.addEventListener("pointermove", onPointerMove);
  el.addEventListener("pointerup", onPointerUp);
  el.addEventListener("pointercancel", onPointerUp);
  el.addEventListener("wheel", onWheel, { passive: false });

  return () => {
    el.removeEventListener("pointerdown", onPointerDown);
    el.removeEventListener("pointermove", onPointerMove);
    el.removeEventListener("pointerup", onPointerUp);
    el.removeEventListener("pointercancel", onPointerUp);
    el.removeEventListener("wheel", onWheel);
  };
}
